import type { AxiosInstance } from "axios";
import { createAuthenticatedApiClient } from "../api/axios";
import { getAppSettings } from "../config/app-settings";

/**
 * User-management API client (same source MerchantApp uses). The access token
 * for ShipBob.OneId.User is a reference token, so OneID claims (org, role,
 * permissions) are only available through this API, never client-side.
 */

export interface Organization {
  OrganizationId?: number;
  OrganizationName?: string;
  ExternalTypeId?: string;
  ParentOrganizationId?: number | null;
}

export interface DetailedUserData {
  UserId?: number;
  Email?: string;
  Name?: string;
  IsAdmin?: boolean;
  /** JSON-encoded OneID claim. Some environments return it already parsed. */
  OneIdClaim?: string | OneIdClaim;
  Roles?: string[];
  Organizations?: Organization[];
}

/** Fields of the OneID claim the host reads. Everything else passes through. */
export interface OneIdClaim {
  sub?: string;
  org_id?: string;
  org_name?: string;
  Lorg?: string;
  lorg_type?: string;
  Morg?: string;
  Perm?: string[];
  perm_names?: string[];
  features?: string[];
  org_atts?: {
    resource_id?: string | null;
    external_type_id?: string | null;
    parent_org_id?: string | null;
  };
  [key: string]: unknown;
}

export function parseOneIdClaim(raw: DetailedUserData["OneIdClaim"]): OneIdClaim | null {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw) as OneIdClaim;
  } catch {
    console.warn("[User] Failed to parse OneIdClaim");
    return null;
  }
}

let client: AxiosInstance | undefined;

function getClient(): AxiosInstance {
  const { userManagementApiUrl } = getAppSettings();
  if (!userManagementApiUrl) {
    throw new Error("[User] userManagementApiUrl is not configured in appsettings");
  }
  client ??= createAuthenticatedApiClient(userManagementApiUrl);
  return client;
}

export function isUserManagementConfigured(): boolean {
  return Boolean(getAppSettings().userManagementApiUrl);
}

export async function fetchDetailedUserData(): Promise<DetailedUserData> {
  const { data } = await getClient().get<DetailedUserData>("/api/userData/fetchDetailedUserData");
  return data;
}

export async function fetchImpersonatedDetailedUserData(): Promise<DetailedUserData> {
  const { data } = await getClient().get<DetailedUserData>(
    "/api/userData/fetchImpersonatedDetailedUserData",
  );
  return data;
}
